import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Snackbar, Alert, AlertColor } from '@mui/material';

interface ToastInput {
    message: string;
    severity?: AlertColor;
    duration?: number;
}

interface ToastContextShape {
    showToast: (input: ToastInput) => void;
}

const ToastContext = createContext<ToastContextShape>({
    showToast: () => {
        /* placeholder default */
    },
});

interface ToastState {
    open: boolean;
    message: string;
    severity: AlertColor;
    duration: number;
}

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [state, setState] = useState<ToastState>({
        open: false,
        message: '',
        severity: 'success',
        duration: 3500,
    });

    const showToast = useCallback(
        ({ message, severity = 'success', duration = 3500 }: ToastInput) => {
            setState({ open: true, message, severity, duration });
        },
        [],
    );

    const handleClose = useCallback(() => setState((s) => ({ ...s, open: false })), []);

    const value = useMemo(() => ({ showToast }), [showToast]);

    return (
        <ToastContext.Provider value={value}>
            {children}
            <Snackbar
                open={state.open}
                autoHideDuration={state.duration}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleClose}
                    severity={state.severity}
                    variant='filled'
                    sx={{ width: '100%' }}
                >
                    {state.message}
                </Alert>
            </Snackbar>
        </ToastContext.Provider>
    );
};

export const useToast = (): ToastContextShape => useContext(ToastContext);
